import moment from "moment";
import find from "lodash/find";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import map from "lodash/map";
import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import mapValues from "lodash/mapValues";
import orderBy from "lodash/orderBy";
import sumBy from "lodash/sumBy";

import { eachDayOfInterval, format, getDay, isAfter, isBefore, isSameDay } from "date-fns";

import * as dateUtil from "./date";
import { hasNoLiveFilters } from "./filters";

const YMD = "yyyy-MM-dd";

const START_LIMIT = dateUtil.HISTORY_MAX.clone().startOf("day").toDate();

/**
 * @param {object} filters
 * @param {?string} filters.startDate
 * @param {?string} filters.endDate
 * @param {object[]} entries
 * @param {object} options
 * @param {boolean} options.hasMore
 * @param {?number} options.page
 * @return {object} a range with `start` and `end`
 */
export function dateRangeFor(filters, entries, options) {
  let [start, end] = initRangeFor(filters);

  const earliestDate = earliestDateIn(entries);
  const latestDate = latestDateIn(entries);

  if (earliestDate && isBefore(earliestDate, start)) {
    start = earliestDate;
  }

  if (isBefore(start, START_LIMIT)) {
    start = START_LIMIT;
  }

  if (latestDate && isAfter(latestDate, end)) {
    end = latestDate;
  }

  return { start, end };
}

/**
 * Yield a specialized `Day` object
 * @param {object} range
 * @param {Date} range.start
 * @param {Date} range.end
 * @return {void}
 */
export function* eachDayInRange(range) {
  for (const date of eachDayOfInterval(range)) {
    const key = format(date, YMD);
    const number = dateUtil.getDayNumber(date);

    const day = { date, key, number };

    yield day;
  }
}

/**
 * @param {object[]} entries
 * @return {?Date}
 */
export function earliestDateIn(entries) {
  const earliest = earliestEntryIn(entries);

  return get(earliest, "actualDate", null);
}

/**
 * @param {object[]} entries
 * @return {?string}
 */
export function earliestDayIn(entries) {
  const earliest = earliestEntryIn(entries);

  return get(earliest, "stringDate", null);
}

/**
 * @param {object[]} entries
 * @return {?object}
 */
export function earliestEntryIn(entries) {
  if (!isEntries(entries)) {
    return null;
  }

  return minBy(entries, "stringDate");
}

/**
 * @param {object[]} entries
 * @return {?moment}
 */
export function earliestMomentIn(entries) {
  const earliest = earliestEntryIn(entries);

  return get(earliest, "entryDate", null);
}

/**
 * @param {object[]} entries
 * @return {boolean}
 */
export function isEntries(entries) {
  return Array.isArray(entries);
}

/**
 * @param {object[]} entries
 * @return {?Date}
 */
export function latestDateIn(entries) {
  const latest = latestEntryIn(entries);

  return get(latest, "actualDate", null);
}

/**
 * @param {object[]} entries
 * @return {?string}
 */
export function latestDayIn(entries) {
  const latest = latestEntryIn(entries);

  return get(latest, "stringDate", null);
}

/**
 * @param {object[]} entries
 * @return {?object}
 */
export function latestEntryIn(entries) {
  if (!isEntries(entries)) {
    return null;
  }

  return maxBy(entries, "stringDate");
}

/**
 * @param {object[]} entries
 * @return {?moment}
 */
export function latestMomentIn(entries) {
  const latest = latestEntryIn(entries);

  return get(latest, "entryDate", null);
}

/**
 * @param {object[]} entries
 * @param {object} filters
 * @param {?string} filters.startDate
 * @param {?string} filters.endDate
 * @param {object} options
 * @param {boolean} options.hasMore
 * @param {?number} options.page
 * @param {object} options.workingHours
 * @return {object[]}
 */
export function maybeWithNoEntries(entries, filters, options) {
  if (!isEntries(entries)) {
    return [];
  }

  if (!hasNoLiveFilters(filters)) {
    return entries;
  }

  const { workingHours } = options;

  const newEntries = [...entries];

  const hoursPerDay = sumHoursWorkedPerDay(entries);

  const range = dateRangeFor(filters, entries, options);

  for (const { key, number } of eachDayInRange(range)) {
    const hoursWorked = get(hoursPerDay, key, 0);
    const enabled = get(workingHours, [number, "enabled"], false);

    if (hoursWorked > 0 || !enabled) {
      continue;
    }

    newEntries.push(noEntriesFor(key));
  }

  return orderEntries(newEntries, filters);
}

/**
 * @param {object} response
 * @param {object} response.sideLoadedData
 * @param {object[]} response.timeEntries
 * @param {object[]} categories
 * @return {object[]}
 */
export function normalizeEntries(response, categories) {
  if (!response || !Array.isArray(response.timeEntries)) {
    return [];
  }

  const { sideLoadedData } = response;

  return response.timeEntries.map(function prepareEntry(t) {
    const [account, hasAccount] = getHas(sideLoadedData, "accounts", t.accountId);
    const [productGroup, hasProductGroup] = getHas(sideLoadedData, "productGroups", t.productGroupId);

    let opportunity = {};
    let productLine = {};

    if (hasAccount) {
      opportunity = find(account.opportunities, { opportunityId: t.opportunityId }) || {};
    }

    if (hasProductGroup) {
      productLine = find(productGroup.productLines, { productLineId: t.productLineId }) || {};
    }

    const location = get(sideLoadedData, ["locations", t.locationId], {});
    const distributor = get(sideLoadedData, ["distributors", t.distributorId], {});
    const serviceRequest = get(sideLoadedData, ["serviceRequests", t.serviceRequestId], {});
    const interlock = get(sideLoadedData, ["interlocks", t.interlockId], {});
    const categoryObject = find(categories, { id: t.categoryId }) || {};

    const entryDate = dateUtil.stringApiToMoment(t.entryDate);
    const actualDate = entryDate.clone().startOf("day").toDate();
    const stringDate = entryDate.format("YYYY-MM-DD");

    return normalizeEntry({
      ...t,
      account,
      categoryObject,
      distributor,
      interlock,
      location,
      opportunity,
      productGroup,
      productLine,
      serviceRequest,
    });
  });
}

/**
 * @param {object} entry
 * @param {moment, string}
 * @return {object} the normalized entry, with actual date and string date keys
 */
export function normalizeEntry(entry) {
  const entryDate = normalizeEntryDate(get(entry, "entryDate"));

  if (!entryDate) {
    console.warn("Unknown entry date");
    console.dir({ entry });

    return null;
  }

  const actualDate = entryDate.clone().startOf("day").toDate();
  const stringDate = entryDate.format("YYYY-MM-DD");

  // TODO: This gets duplicated to this property for some reason. Let's remove at some point
  const submitted = get(entry, "submitDate", null);

  return { ...entry, entryDate, actualDate, stringDate, key: stringDate, submitted };
}

/**
 * @param {moment, string} date
 * @return {object}
 */
export function noEntriesFor(date) {
  const entryDate = normalizeEntryDate(date);

  const entry = {
    entryDate,
    hours: 0,
    noEntries: true
  };

  return normalizeEntry(entry);
}

/**
 * @param {object[]} entries
 * @param {object} filters
 * @param {string} filters.sortBy
 * @return {object[]}
 */
export function orderEntries(entries, filters) {
  const sortDir = get(filters, "sortDir", "asc");

  return orderBy(entries, ["stringDate"], [sortDir]);
}

/**
 * @param {object[]} entries
 * @param {object} filters
 * @param {string} filters.sortBy
 * @return {?string}
 */
export function padDayFor(entries, filters) {
  if (!isEntries(entries)) {
    return null;
  }

  const sortDir = get(filters, "sortDir", "asc");

  const fn = sortDir === "asc" ? latestMomentIn : earliestMomentIn;

  const pad = fn(entries);

  return pad ? dateUtil.dateToApiString(pad) : null;
}

export function roundHours(hours) {
  hours = Number(hours);
  const rounded = (Math.round(hours * 2) / 2);
  return rounded > 24 ? 24 : rounded.toFixed(1);
}

/**
 * @param {object[]} entries
 * @return {object.<string,number>}
 */
export function sumHoursWorkedPerDay(entries) {
  const grouped = groupBy(entries, "stringDate");

  return mapValues(grouped, sumHoursWorkedFor)
}

/**
 * @param {object[]} entries
 * @return {number}
 */
export function sumHoursWorkedFor(entries) {
  return sumBy(entries, "hours");
}

const NOT_LOADED = Symbol("NOT_LOADED");

function getHas(source, ...keys) {
  const actual = get(source, keys, NOT_LOADED);

  return actual !== NOT_LOADED ? ([actual, true]) : ([{}, false]);
}

/**
 * @param {object} filters
 * @param {?string} filters.startDate
 * @param {?string} filters.endDate
 * @return {Date[]}
 */
function initRangeFor(filters) {
  const start = initRangeStartFor(filters);
  const end = initRangeEndFor(filters);

  if (!isSameDay(start, end) && isAfter(start, end)) {
    return [end, start];
  }

  return [start, end];
}

/**
 * @param {object} filters
 * @param {?string} filters.startDate
 * @return {Date}
 */
function initRangeStartFor({ startDate }) {
  const start = (startDate)
    ? dateUtil.stringApiToMoment(startDate)
    : dateUtil.HISTORY_MAX.clone()
  ;

  return start.startOf("day").toDate();
}

/**
 * @param {object} filters
 * @param {?string} filters.endDate
 * @return {Date}
 */
function initRangeEndFor({ endDate }) {
  const end = (endDate)
    ? dateUtil.stringApiToMoment(endDate)
    : moment()
  ;

  return end.startOf("day").toDate()
}

/**
 * @param {moment, string} value
 * @return {moment}
 */
function normalizeEntryDate(value) {
  if (moment.isMoment(value)) {
    return value.clone();
  }

  if (typeof value === "string") {
    return dateUtil.stringApiToMoment(value);
  } else if (value instanceof Date) {
    return moment(value);
  }

  return null;
}



// WEBPACK FOOTER //
// ./app/utils/entry.js